import {Injectable} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ComplianceCalculateService} from './wizard-form-groups-services/compliance-calculate.service';
import {NtdService} from './wizard-form-groups-services/ntd.service';
import {OrganizationCommonService} from './wizard-form-groups-services/nested-forms/organization-common.service';
import {OrganizationLegalService} from './wizard-form-groups-services/nested-forms/organization-legal.service';
import {OrganizationBankService} from './wizard-form-groups-services/nested-forms/organization-bank.service';
import {OrganizationContactsService} from './wizard-form-groups-services/nested-forms/organization-contacts.service';
import {LaboratoryTypeService} from './wizard-form-groups-services/nested-forms/laboratory-type.service';
import {WizardNestedFormService} from './wizard-form-groups-services/abstract/wizard-nested-form-service';
import {LaboratoryOwnerService} from './wizard-form-groups-services/nested-forms/laboratory-owner.service';
import {LaboratoryCommonService} from './wizard-form-groups-services/nested-forms/laboratory-common.service';
import {AttestationService} from './wizard-form-groups-services/nested-forms/attestation.service';
import {LaboratoryStaffService} from './wizard-form-groups-services/nested-forms/laboratory-staff.service';
import {EquipmentService} from './wizard-form-groups-services/nested-forms/equipment.service';
import {FormHelperService} from '@services/form-helper.service';
import {ResponsibleStaffService} from './wizard-form-groups-services/nested-forms/responsible-staff.service';
import {ConclusionsService} from './wizard-form-groups-services/nested-forms/conclusions.service';
import {ResultService} from './wizard-form-groups-services/nested-forms/result.service';

/**
 * Сервис для реализации внутренней логики работы формы визарда
 */

@Injectable()
export class WizardFormService {
    form: FormGroup;

    /**
     * Вложенные сервисы, содержащие части большой формы
     */
    private nestedServices: WizardNestedFormService[] = [];

    constructor(
        private _fb: FormBuilder,
        private complianceCalculateService: ComplianceCalculateService,
        private ntdService: NtdService,

        private organizationCommon: OrganizationCommonService,
        private organizationLegal: OrganizationLegalService,
        private organizationBank: OrganizationBankService,
        private organizationContacts: OrganizationContactsService,
        private laboratoryType: LaboratoryTypeService,
        private laboratoryOwner: LaboratoryOwnerService,
        private laboratoryCommon: LaboratoryCommonService,
        private attestation: AttestationService,
        private laboratoryStaff: LaboratoryStaffService,
        private equipment: EquipmentService,
        private responsibleStaff: ResponsibleStaffService,
        private conclusionsService: ConclusionsService,
        private resultService: ResultService,
    ) {
        this.initForm(_fb);

        this.nestedServices = [
            this.organizationCommon,
            this.organizationLegal,
            this.organizationBank,
            this.organizationContacts,
            this.laboratoryType,
            this.laboratoryOwner,
            this.laboratoryCommon,
            this.attestation,
            this.laboratoryStaff,
            this.equipment,
            this.responsibleStaff,
            this.ntdService,
            this.conclusionsService,
            this.resultService,
        ];

        const organizationGroup = this.form.get('organization') as FormGroup;

        organizationGroup.setControl('common', this.organizationCommon.form);
        organizationGroup.setControl('legal', this.organizationLegal.form);
        organizationGroup.setControl('bank', this.organizationBank.form);
        organizationGroup.setControl('contacts', this.organizationContacts.form);

        const laboratoryGroup = this.form.get('laboratory') as FormGroup;

        laboratoryGroup.setControl('type', this.laboratoryType.form);
        laboratoryGroup.setControl('owner', this.laboratoryOwner.form);
        laboratoryGroup.setControl('common', this.laboratoryCommon.form);

        this.form.setControl('attestation', this.attestation.form);
        this.form.setControl('laboratory_staff', this.laboratoryStaff.form);
        this.form.setControl('responsible_staff', this.responsibleStaff.form);

        /**
         * Расчет таблицы соотвествия делается немного вне сервиса шага персонала, так как лучшего решения
         * в условях текущего состава формы я не нашел
         */
        this.complianceCalculateService.fbControl = this.form.get('laboratory_staff').get('isCovered') as FormControl;
        this.complianceCalculateService.init();

        this.form.setControl('equipment', this.equipment.equipmentFormArray);
        this.form.setControl('reference', this.equipment.referenceFormArray);

        this.ntdService.formCommon = this.form.get('ntd_common') as FormGroup;
        this.ntdService.formMethods = this.form.get('ntd_methods') as FormGroup;
        this.ntdService.formObjects = this.form.get('ntd_objects') as FormGroup;

        this.form.setControl('conclusions', this.conclusionsService.form);
        this.form.setControl('result', this.resultService.form);
    }

    private initForm(fb: FormBuilder) {
        /**
         * Отображаем общую укрупненную структуру формы.
         * Конкретная реализация каждой группы находится в своем вложенном сервисе
         */
        this.form = fb.group({
            organization: fb.group({
                /**
                 * Общие данные организации
                 */
                common: fb.group({}),

                /**
                 * Юридические реквизиты
                 */
                legal: fb.group({}),

                /**
                 * Банковские реквизиты
                 */
                bank: fb.group({}),

                /**
                 * Контактные данные
                 */
                contacts: fb.group({}),
            }),
            laboratory: fb.group({

                /**
                 * Тип лаборатории
                 */
                type: fb.group({}),

                owner: fb.group({}),

                /**
                 * Общие данные лаборатории
                 */
                common: fb.group({}),
            }),

            /**
             * Область аттестации
             */
            attestation: fb.group({}),

            /**
             * Персонал лаборатории
             */
            laboratory_staff: fb.group({}),

            /**
             * Ответственные лица
             */
            responsible_staff: fb.group({}),

            /**
             * Оборудование
             */
            equipment: fb.array([]),

            /**
             * Образцы и эталоны
             */
            reference: fb.array([]),

            /**
             * Общие НТД
             */
            ntd_common: fb.group({
                _touched: [false, Validators.requiredTrue],
            }),

            /**
             * НТД по методам
             */
            ntd_methods: fb.group({
                _touched: [false, Validators.requiredTrue],
            }),

            /**
             * НТД по объектам
             */
            ntd_objects: fb.group({
                _touched: [false, Validators.requiredTrue],
            }),

            /**
             * Заключительные положения
             */
            conclusions: fb.group({}),

            /**
             * Результат
             */
            result: fb.group({}),
        });
    }

    private restoreValidators() {
        this.nestedServices.forEach((service: WizardNestedFormService) => {
            if (service.restoreValidators) {
                service.restoreValidators();
            }
        });

        this.nestedServices.forEach((service: WizardNestedFormService) => {
            if (service.init) {
                service.init();
            }
        });
    }

    public afterDataRestoreHandler() {
        this.restoreValidators();
        /**
         * Проводим валидацию всех шагов, с которыми уже работали
         */
        this.nestedServices.forEach(service => {
            if (service.form && service.isFormTouched()) {
                FormHelperService.validateGroup(service.form);
            }
        });
    }
}
